import { Divider } from "@mui/material";
import {
    createContributionPendingChange,
    canChangeInvestmentStrategySelector,
    changeContributionStateSelector,
    hasQuestionnaireSelector,
    questionnaireRejectedSelector,
    setChangeContributionSuccessful,
    upsertContributionPendingChange,
    useAppDispatch,
    useAppSelector,
    useChangeContributionMutation,
    whatToChangeAnswersSelector,
} from "@repo/logic";
import { FC, useEffect, useMemo, useRef } from "react";
import { NumberToCZK } from "../../helpers";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { Card } from "../Cards";
import { FormErrorMessage } from "../FormErrorMessage";
import { PensionQuestionnaireStepProps } from "../PensionQuestionnaire";
import { DefaultStepProps } from "../Stepper/CustomStepper";
import { BodyTextM } from "../Typography";
import * as Styled from "./ChangeContributionRecapPage.styled";

type ChangeContributionRecapPageProps = PensionQuestionnaireStepProps & DefaultStepProps;

enum RecapState {
    QuestionnaireRejected = "QuestionnaireRejected",
    NoStrategyChange = "NoStrategyChange",
    NoStrategyChangeByClient = "NoStrategyChangeByClient",
    StrategyChange = "StrategyChange",
}

export const ChangeContributionRecapPage: FC<ChangeContributionRecapPageProps> = ({
    setModalTitle,
    hidePrevButton,
    setContinueCondition,
    contractId,
}) => {
    const { t } = useTypedTranslation();
    const dispatch = useAppDispatch();
    const changeContributionState = useAppSelector(changeContributionStateSelector);
    const questionnaireRejected = useAppSelector(questionnaireRejectedSelector);
    const canChangeInvestmentStrategy = useAppSelector(canChangeInvestmentStrategySelector);
    const whatToChangeAnswers = useAppSelector(whatToChangeAnswersSelector);
    const hasQuestionnaire = useAppSelector(hasQuestionnaireSelector);
    const { mutateAsync: changeContribution } = useChangeContributionMutation();

    // Use ref to avoid re-registering setContinueCondition on every Redux state change
    const contributionStateRef = useRef(changeContributionState);
    contributionStateRef.current = changeContributionState;

    const state = useMemo((): RecapState => {
        if (questionnaireRejected === true) return RecapState.QuestionnaireRejected;
        if (whatToChangeAnswers?.existingContribution === false && whatToChangeAnswers?.newContribution === false)
            return RecapState.NoStrategyChangeByClient;
        if (hasQuestionnaire && canChangeInvestmentStrategy !== false) return RecapState.NoStrategyChange;
        return RecapState.StrategyChange;
    }, [questionnaireRejected, canChangeInvestmentStrategy, whatToChangeAnswers, hasQuestionnaire]);

    // Change title of modal and keep it for later steps, and hide previous button
    useEffect(() => {
        setModalTitle?.(t("investmentStrategy.changePensionContribution.stepperTitle"));
        hidePrevButton?.();
    }, [hidePrevButton, setModalTitle, t]);

    // Submit data to backend when user clicks "Continue" on this recap page
    useEffect(() => {
        setContinueCondition?.(async () => {
            if (!contractId) return false;
            try {
                await changeContribution({
                    contractId,
                    data: {
                        contribution: contributionStateRef.current?.newMonthlyContribution,
                        doesEmployerContribute: contributionStateRef.current?.newEmployerContribute,
                    },
                });
                if (contributionStateRef.current) {
                    dispatch(
                        upsertContributionPendingChange({
                            contractId,
                            pendingChange: createContributionPendingChange({
                                monthlyContribution: contributionStateRef.current.newMonthlyContribution,
                                doesEmployerContribute: contributionStateRef.current.newEmployerContribute,
                            }),
                        }),
                    );
                }
                dispatch(setChangeContributionSuccessful(true));
                return true;
            } catch {
                dispatch(setChangeContributionSuccessful(false));
                return false;
            }
        });
    }, [setContinueCondition, changeContribution, contractId, dispatch]);

    return (
        <Styled.MainWrapper>
            <Styled.PageHeading>{t(`employerContributeDialog.recap.title${state}`)}</Styled.PageHeading>
            <Card>
                <Styled.CardInnerStack divider={<Divider />}>
                    <Styled.StackEqualCols>
                        <Styled.StackColItem>
                            <Styled.BoldBodyTextM>{t("employerContributeDialog.newContribution")}</Styled.BoldBodyTextM>
                        </Styled.StackColItem>
                        <Styled.StackColItem>
                            <BodyTextM>{NumberToCZK(changeContributionState?.newMonthlyContribution ?? 0)}</BodyTextM>
                        </Styled.StackColItem>
                    </Styled.StackEqualCols>

                    <Styled.StackEqualCols>
                        <Styled.StackColItem>
                            <Styled.BoldBodyTextM>
                                {t("employerContributeDialog.employerContributes")}
                            </Styled.BoldBodyTextM>
                        </Styled.StackColItem>
                        <Styled.StackColItem>
                            <BodyTextM>
                                {changeContributionState?.newEmployerContribute === true
                                    ? t("global.yes")
                                    : t("global.no")}
                            </BodyTextM>
                        </Styled.StackColItem>
                    </Styled.StackEqualCols>
                </Styled.CardInnerStack>
            </Card>

            {state === RecapState.QuestionnaireRejected && (
                <FormErrorMessage severity="info">
                    {t("employerContributeDialog.recap.titleQuestionnaireRejectedInfo")}
                </FormErrorMessage>
            )}
            {state === RecapState.NoStrategyChange && (
                <FormErrorMessage severity="info">
                    {t("employerContributeDialog.recap.titleNoStrategyChangeInfo")}
                </FormErrorMessage>
            )}
            {state === RecapState.NoStrategyChangeByClient && (
                <FormErrorMessage severity="info">
                    {t("employerContributeDialog.recap.titleNoStrategyChangeByClientInfo")}
                </FormErrorMessage>
            )}
        </Styled.MainWrapper>
    );
};
